import { gt } from '../i18n/index.js';
import { escapeHtml, flag, playerLocalizedName } from './helpers.js';

export function finalScoreParts(match) {
  if (!match?.score) return { home: 0, away: 0 };
  const extra = match.extraTimeScore;
  return { home: Number(match.score?.home || 0) + Number(extra?.home || 0), away: Number(match.score?.away || 0) + Number(extra?.away || 0) };
}

export function finalScore(match) {
  if (!match?.score) return 'vs';
  const score = finalScoreParts(match);
  const marker = scoreDecisionMarker(match);
  if (!marker) return `${score.home}-${score.away}`;
  const winnerId = match.penaltyShootout ? match.penaltyWinnerTeamId : match.winnerTeamId;
  return winnerId === match.homeTeamId ? `${marker} ${score.home}-${score.away}` : `${score.home}-${score.away} ${marker}`;
}

export function finalScoreHtml(match) {
  if (!match?.score) return '<span class="score-vs">VS</span>';
  const score = finalScoreParts(match);
  const marker = scoreDecisionMarker(match);
  if (!marker) return `${score.home}-${score.away}`;
  const markerHtml = `<span class="score-marker">${marker}</span>`;
  const winnerId = match.penaltyShootout ? match.penaltyWinnerTeamId : match.winnerTeamId;
  return winnerId === match.homeTeamId ? `${markerHtml} ${score.home}-${score.away}` : `${score.home}-${score.away} ${markerHtml}`;
}

export function scoreDecisionMarker(match) {
  if (match.penaltyShootout) return gt('match.penaltyMarker');
  if (match.extraTimeScore) return gt('match.extraTimeMarker');
  return '';
}

export function eliminatedTeamId(match) {
  if (!match || match.stage === 'Group Stage' || !match.score) return '';
  const winnerId = match.winnerTeamId || match.penaltyWinnerTeamId;
  if (!winnerId) return '';
  return winnerId === match.homeTeamId ? match.awayTeamId : match.homeTeamId;
}

export function matchDecisionSuffix(match) {
  if (match.penaltyShootout) return gt('match.decision.penalty', { home: match.penaltyShootout.score.home, away: match.penaltyShootout.score.away });
  if (match.extraTimeScore) return gt('match.decision.extra', { home: match.extraTimeScore.home, away: match.extraTimeScore.away });
  return '';
}

export function matchDecisionNote(match, teamNameFn) {
  if (match.penaltyShootout) return `<div class="bracket-note">${gt('match.shootout')} ${match.penaltyShootout.score.home}-${match.penaltyShootout.score.away}：${escapeHtml(teamNameFn(match.penaltyWinnerTeamId))}</div>`;
  if (match.extraTimeScore) return `<div class="bracket-note">${gt('match.extraTime')} ${match.extraTimeScore.home}-${match.extraTimeScore.away}</div>`;
  return '';
}

export function matchDecisionSummary(match, teamNameFn) {
  if (match.penaltyShootout) {
    const normal = `${match.score.home}-${match.score.away}`;
    const extra = `${match.extraTimeScore.home}-${match.extraTimeScore.away}`;
    const penalty = `${match.penaltyShootout.score.home}-${match.penaltyShootout.score.away}`;
    const subtitle = gt('match.modal.subtitle.penalty', { normal, extra, penalty, winner: escapeHtml(teamNameFn(match.penaltyWinnerTeamId)) });
    return `<p class="modal-subtitle">${subtitle}</p>`;
  }
  if (match.extraTimeScore) {
    const normal = `${match.score.home}-${match.score.away}`;
    const extra = `${match.extraTimeScore.home}-${match.extraTimeScore.away}`;
    const subtitle = gt('match.modal.subtitle.extra', { normal, extra });
    return `<p class="modal-subtitle">${subtitle}</p>`;
  }
  return '';
}

export function matchDecisionNewsSentence(match, teamNameFn) {
  if (match.penaltyShootout) {
    const normal = `${match.score.home}-${match.score.away}`;
    const extra = match.extraTimeScore ? `${match.extraTimeScore.home}-${match.extraTimeScore.away}` : normal;
    const penalty = `${match.penaltyShootout.score.home}-${match.penaltyShootout.score.away}`;
    return gt('match.news.penalty', { normal, extra, penalty, winner: teamNameFn(match.penaltyWinnerTeamId) });
  }
  if (match.extraTimeScore) {
    const score = finalScoreParts(match);
    const normal = `${match.score.home}-${match.score.away}`;
    const extra = `${match.extraTimeScore.home}-${match.extraTimeScore.away}`;
    const goals = String(match.extraTimeScore.home + match.extraTimeScore.away);
    return gt('match.news.extraTime', { normal, extra, goals, final: `${score.home}-${score.away}` });
  }
  return '';
}

export function fixtureMeta(fixture) {
  return [
    fixture.stage ? gt(`stage.${fixture.stage}`) : '',
    fixture.group ? gt('landing.groupLabel', { id: fixture.group }) : '',
    fixture.matchday ? gt('match.roundLabel', { n: fixture.matchday }) : '',
    fixture.date || '',
    fixture.venue || '',
  ].filter(Boolean).join(' · ');
}

export function matchGoalEvents(match) {
  if (!match) return [];
  return (match.timeline || [])
    .filter((event) => event.eventType === 'goal')
    .sort((a, b) => Number(a.minute || 0) - Number(b.minute || 0))
    .map((event, index) => ({
      key: `${match.id}-${event.minute}-${event.playerId || index}`,
      minute: event.minute,
      teamId: event.teamId,
      player: goalPlayerName(match, event),
    }));
}

export function matchTeamGoalEvents(match, teamId) {
  return matchGoalEvents(match).filter((goal) => goal.teamId === teamId);
}

export function matchCardEvents(match) {
  if (!match) return [];
  return (match.timeline || [])
    .filter((event) => ['redCard', 'yellowCard', 'injury'].includes(event.eventType))
    .sort((a, b) => Number(a.minute || 0) - Number(b.minute || 0))
    .map((event, index) => ({
      key: `${match.id}-${event.eventType}-${event.minute}-${event.playerId || index}`,
      minute: event.minute,
      stoppageTime: event.stoppageTime,
      teamId: event.teamId,
      player: cardPlayerName(match, event),
      eventType: event.eventType,
    }));
}

export function matchTeamCardEvents(match, teamId) {
  return matchCardEvents(match).filter((card) => card.teamId === teamId);
}

function cardPlayerName(match, event) {
  if (event.playerId) {
    const player = (match.playerRatings || []).find((item) => item.playerId === event.playerId);
    if (player) return playerLocalizedName(player);
  }
  return event.description || gt('helpers.unknownPlayer');
}

export function goalPlayerName(match, event) {
  if (event.playerId) {
    const player = (match.playerRatings || []).find((item) => item.playerId === event.playerId);
    if (player) return playerLocalizedName(player);
  }
  return String(event.description || '').replace(/\s+scores.*$/i, '') || gt('helpers.unknownPlayer');
}

export function timelineScoreHtml(item, flagFn) {
  return `${flagFn(item.fixture.homeTeamId)} ${item.match ? finalScoreHtml(item.match) : '<span class="score-vs"></span>'} ${flagFn(item.fixture.awayTeamId)}`;
}

export function teamScoreLabel(match, teamId) {
  if (!match?.score) return '';
  const score = finalScoreParts(match);
  const value = teamId === match.homeTeamId ? score.home : score.away;
  const marker = scoreDecisionMarker(match);
  const winnerId = match.penaltyShootout ? match.penaltyWinnerTeamId : match.winnerTeamId;
  if (!marker || winnerId !== teamId) return String(value);
  const markerHtml = `<span class="score-marker">${marker}</span>`;
  return teamId === match.homeTeamId ? `${markerHtml}${value}` : `${value}${markerHtml}`;
}

export function matchEvents(match) {
  const items = (match.timeline || [])
    .filter((event) => ['goal', 'bigChance', 'redCard', 'yellowCard'].includes(event.eventType))
    .slice(0, 18)
    .map((event) => `<span class="event">${event.minute}' ${escapeHtml(event.description)}</span>`)
    .join('');
  return items || `<span class="event">${gt('match.eventsFallback')}</span>`;
}

export function renderShootoutDetails(match, teamNameFn) {
  if (!match.penaltyShootout) return '';
  return `<div class="shootout-detail"><h3>${gt('match.shootout')}</h3><div class="shootout-kicks">${match.penaltyShootout.kicks.map((kick) => `<div class="shootout-kick ${kick.scored ? 'scored' : 'missed'}"><span>${flag(kick.teamId)}</span><strong>${escapeHtml(kick.playerName)}</strong><em>${kick.scored ? gt('match.penalty.scored') : gt('match.penalty.missed')}</em></div>`).join('')}</div></div>`;
}

export function totalGoals(match) {
  const score = finalScoreParts(match);
  return score.home + score.away;
}

export function goalsForTeam(match, teamId) {
  const score = finalScoreParts(match);
  return match.homeTeamId === teamId ? score.home : score.away;
}

export function goalsAgainstTeam(match, teamId) {
  const score = finalScoreParts(match);
  return match.homeTeamId === teamId ? score.away : score.home;
}

export function opponentId(match, teamId) {
  return match.homeTeamId === teamId ? match.awayTeamId : match.homeTeamId;
}

export function simulationCloseness(match) {
  const xgDelta = Math.abs((match.xgHome || 0) - finalScoreParts(match).home) + Math.abs((match.xgAway || 0) - finalScoreParts(match).away);
  const value = Math.max(62, Math.min(98, Math.round(96 - xgDelta * 9)));
  return `${value}%`;
}

export function topPlayer(match) {
  const player = (match.playerRatings || [])[0];
  return player ? `${playerLocalizedName(player)} ${Number(player.rating || 0).toFixed(1)}` : 'N/A';
}
